import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketType,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { useMemo, useState } from 'react';
import { getSplitOrders } from 'src/utils/scaledOrders';
import usePerpTradeConfirmationModalHandlers from 'src/components/TradeConfirmationModal/hooks/usePerpTradeConfirmationModalHandlers';
import useTradeConfirmationModal from 'src/components/TradeConfirmationModal/hooks/useTradeConfirmationModal';
import { TradeConfirmationModalProps } from 'src/components/TradeConfirmationModal/types';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

export default function usePerpTradeConfirmationModal({
	tradeFormDetails,
}: TradeConfirmationModalProps) {
	const [isSubmitting, setIsSubmitting] = useState(false);

	const {
		orderType,
		postOnly,
		reduceOnly,
		bracketOrders,
		baseSizeStringValue,
		quoteSizeStringValue,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		side: tradeSide,
		market: selectedMarketId,
		scaledOrders: { orderCount, sizeDistribution },
	} = tradeFormDetails;

	const {
		selectedMarket,
		actionValue,
		actionValueClassName,
		sizeDisplayLabel,
		sizeDisplayValue,
		estimatedLiquidationPriceUi,
		stepSizeDecimalPlaces,
		tickSizeDecimalPlaces,
		currentStepSize,
		priceBoxValue,
		secondaryPriceBoxValue,
	} = useTradeConfirmationModal({
		marketType: MarketType.PERP,
		orderType,
		tradeSide,
		baseSizeStringValue,
		quoteSizeStringValue,
		selectedMarketId,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
	});

	const baseSize = useMemo(
		() => parseInputBigNumOrZero(baseSizeStringValue, BASE_PRECISION_EXP),
		[baseSizeStringValue]
	);

	const tradeCurrency = selectedMarket.uiSymbols.baseAssetSymbol;

	const currentStepSizeBigNum = useMemo(
		() => BigNum.from(currentStepSize, BASE_PRECISION_EXP),
		[currentStepSize]
	);

	// Exact prices for the scaled-order preview; `priceBoxValue` above is a float.
	const priceBoxBigNum = useMemo(
		() => parseInputBigNumOrZero(priceBoxStringValue, PRICE_PRECISION_EXP),
		[priceBoxStringValue]
	);
	const secondaryPriceBoxBigNum = useMemo(
		() =>
			parseInputBigNumOrZero(secondaryPriceBoxStringValue, PRICE_PRECISION_EXP),
		[secondaryPriceBoxStringValue]
	);

	const scaledOrders = useMemo(() => {
		return getSplitOrders({
			totalSize: baseSize,
			currentStepSize: currentStepSizeBigNum,
			orderCount,
			orderType,
			priceBoxValue: priceBoxBigNum,
			secondaryPriceBoxValue: secondaryPriceBoxBigNum,
			sizeDistribution,
			tradeCurrency,
		});
	}, [
		baseSize,
		currentStepSizeBigNum,
		orderCount,
		orderType,
		priceBoxBigNum,
		secondaryPriceBoxBigNum,
		sizeDistribution,
		tradeCurrency,
	]);

	const { handleClose, handleConfirmation } =
		usePerpTradeConfirmationModalHandlers({
			selectedMarket,
			tradeFormDetails,
			setIsSubmitting,
		});

	return {
		orderType,
		priceBoxValue,
		secondaryPriceBoxValue,
		tradeSide,
		stepSizeDecimalPlaces,
		tickSizeDecimalPlaces,
		actionValue,
		sizeDisplayLabel,
		sizeDisplayValue,
		actionValueClassName,
		estimatedLiquidationPriceUi,
		reduceOnly,
		postOnly,
		orderCount,
		scaledOrders,
		bracketOrders,
		isSubmitting,
		handleClose,
		handleConfirmation,
	};
}
